import type { GivingDetail, GivingInterval } from 'models';
import { Col, Form, FormActions, Modal, Row, Title } from 'ui';
import { useUser } from 'features/auth';
import type { GivingCreateModalType } from '../../giving-create-modal';
import { useGivingCreate } from '../../hooks';
import { AdditionalInfoFormContent, type AdditionalInfoFormValues } from '../additional-info-form-content';
import { BillingInformation, type BillingInformationValues } from '../billing-information';
import { RecurringFormContent, type RecurringFormValues } from '../recurring-form-content';
import { AmountsFormContent } from './amounts-form-content';
import { CustomAmountFormContent } from './custom-amount-form-content';

export type FormValuesAmount = FormValuesWithAmount | FormValuesWithCustomAmount;

export type GivingCreateFormValues = AdditionalInfoFormValues &
  BillingInformationValues &
  FormValuesAmount &
  RecurringFormValues;

type FormValuesWithAmount = {
  amount: number;
  showCustomAmount: false;
};

type FormValuesWithCustomAmount = {
  customAmount: number;
  showCustomAmount: true;
};

type P = {
  giving: GivingDetail;
  initialValues?: Partial<GivingCreateFormValues>;
  interval?: GivingInterval;
  onCancel: () => void;
  onSuccess: (values: GivingCreateFormValues) => void;
  type: 'giveNow' | GivingCreateModalType;
};

const GivingCreateForm = ({ giving, initialValues, interval, onCancel, onSuccess, type }: P) => {
  const user = useUser({ requiredAuth: false });

  const [form] = Form.useForm<GivingCreateFormValues>();
  const { viewerInfoWithCompleteAddress } = useGivingCreate();

  const hasViewerCompleteAddress = Boolean(viewerInfoWithCompleteAddress);

  return (
    <Form<GivingCreateFormValues>
      form={form}
      initialValues={{
        firstName: user?.railsUserData.firstName,
        lastName: user?.railsUserData.lastName,
        profileAddress: hasViewerCompleteAddress,
        ...initialValues,
      }}
      layout="vertical"
      onFinish={onSuccess}
    >
      <Row gutter={[0, 'ms']}>
        {type === 'giveNow' ? null : (
          <Col span={24}>
            <Title level={4} marginBottom>
              Details
            </Title>
          </Col>
        )}
        <Col span={24}>
          <AmountsFormContent<GivingCreateFormValues> amounts={giving.amounts} />
          {!interval || interval === 'one_time' ? <CustomAmountFormContent<GivingCreateFormValues> /> : null}
          {type === 'giveByCheck' || interval ? null : (
            <RecurringFormContent<GivingCreateFormValues> intervals={giving.intervals} />
          )}
          <AdditionalInfoFormContent<GivingCreateFormValues> hideReason={type === 'giveNow'} />
        </Col>
      </Row>
      {type === 'giveNow' ? null : (
        <BillingInformation<GivingCreateFormValues> hasViewerCompleteAddress={hasViewerCompleteAddress} />
      )}
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            children: 'Cancel',
            onClick: onCancel,
          }}
          submit={{ children: 'Continue' }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default GivingCreateForm;
